import { ReactNode, useMemo } from 'react';
import { tv } from 'tailwind-variants';

import { fontStyles } from '@/styles/typography';

import { InfoIconOutline } from '@/ui/Icons/InfoIconOutline';
import { WarningCircleSolidIcon } from '@/ui/Icons/WarningCircleSolidIcon';

interface AlertBoxProps {
  title: ReactNode;
  description: ReactNode;
  level?: 'error' | 'warning' | 'info';
}

const alertStyles = tv({
  base: 'rounded-md py-3.5 px-4 flex flex-col gap-2',
  variants: {
    level: {
      error: 'bg-token-red-300',
      warning: 'bg-token-orange-300',
      info: 'bg-token-gray-300',
    },
  },
  defaultVariants: {
    level: 'error',
  },
});

export const InlineAlertBox = ({ title, description, level = 'error' }: AlertBoxProps) => {
  const icon = useMemo(() => {
    switch (level) {
      case 'error':
        return <WarningCircleSolidIcon height={16} width={16} className="w-4 h-4" />;
      case 'warning':
        return <WarningCircleSolidIcon height={16} width={16} className="w-4 h-4" />;
      case 'info':
        return <InfoIconOutline height={16} width={16} className="w-4 h-4" />;
    }
  }, [level]);

  const textColor =
    level === 'error'
      ? 'text-token-red-1300'
      : level === 'warning'
        ? 'text-token-orange-1100'
        : 'text-token-gray-1200';

  return (
    <div className={alertStyles({ level })} role="alert">
      <div
        className={fontStyles({
          variant: 'LABEL_MD',
          class: [
            'flex',
            'items-center',
            'gap-2',
            'after:content-none',
            'before:content-none',
            textColor,
          ],
        })}
      >
        {icon}
        {title}
      </div>
      <div className={fontStyles({ variant: 'BODY_MD', class: ['flex', textColor] })}>
        {description}
      </div>
    </div>
  );
};
